import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';
import MedicationIcon from './MedicationIcon';

const SearchableSelect = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Search...",
  className = "",
  disabled = false,
  allowClear = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Memoize filtered options for better performance
  const filteredOptions = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    return options.filter(option => {
      const label = typeof option === 'string' ? option : option.label;
      return label.toLowerCase().includes(searchLower);
    });
  }, [options, searchTerm]);

  // Limit displayed options for better performance (show max 50)
  const displayedOptions = useMemo(() => {
    return filteredOptions.slice(0, 50);
  }, [filteredOptions]);

  // Get display value
  const getDisplayValue = () => {
    if (!value) return placeholder;
    const selectedOption = options.find(opt => {
      const optValue = typeof opt === 'string' ? opt : opt.value;
      return optValue === value;
    });
    return typeof selectedOption === 'string' ? selectedOption : selectedOption?.label || placeholder;
  };

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-3 py-2 border rounded-md bg-white text-sm ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-gray-400'
        } ${isOpen ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-300'}`}
      >
        <span className={value ? 'text-gray-900' : 'text-gray-500'}>
          {getDisplayValue()}
        </span>
        <div className="flex items-center gap-1">
          {allowClear && value && !disabled && (
            <X 
              className="w-4 h-4 text-gray-400 hover:text-gray-600" 
              onClick={handleClear}
            />
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Type to search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {displayedOptions.length > 0 ? (
              displayedOptions.map((option, index) => {
                const optValue = typeof option === 'string' ? option : option.value;
                const optLabel = typeof option === 'string' ? option : option.label;
                const isSelected = optValue === value;

                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelect(optValue)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 ${
                      isSelected ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-900'
                    }`}
                  >
                    {option.type && (
                      <MedicationIcon 
                        type={option.type} 
                        size={16} 
                        className="text-gray-600 flex-shrink-0" 
                      />
                    )}
                    <span className="flex-1">{optLabel}</span>
                    {option.isTemplate && (
                      <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded">Your Template</span>
                    )}
                    {option.isCommon && (
                      <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded">Common</span>
                    )}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-6 text-center text-sm text-gray-500">
                No results found
              </div>
            )}
            {/* Show hint if more results available */}
            {filteredOptions.length > 50 && (
              <div className="px-3 py-2 text-center text-xs text-gray-400 border-t bg-gray-50">
                Showing 50 of {filteredOptions.length} results. Type to refine search.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
